import { ADDON_CONTENTS, type HomeworkField } from '@adhd/shared';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon } from '../components/Icons.js';
import { HomeworkFieldList } from '../components/HomeworkFields.js';
import { formatUntil } from '../components/KeepInMind.js';
import { useAi } from '../lib/ai.js';
import { api, ApiError } from '../lib/api.js';

/**
 * Setting up something to keep in mind.
 *
 * THE BOX STARTS EMPTY AND STAYS EMPTY. No placeholder, no example, no "people
 * often write…". The app has no opinion about what is good in another person —
 * the same rule that stopped it suggesting names for life events, pointed the
 * other way.
 *
 * SAYING IT OUT LOUD FILLS THE FORM IN; IT DOES NOT START ANYTHING. The model
 * returns a draft that lands in these same fields, dashed, and nothing is
 * written until he taps Start it. He can change every part of it first. At the
 * `off` rung the control is not offered and the form is filled in by hand,
 * which is the whole feature — speaking is a shortcut, not the thing.
 */
export function HomeworkNew(): JSX.Element {
  const navigate = useNavigate();
  const { canSummarise } = useAi();

  const [title, setTitle] = useState('');
  const [fields, setFields] = useState<HomeworkField[]>([
    { kind: 'paragraph', id: 'f1', label: 'Anything you want to put down' },
  ]);
  const [weeks, setWeeks] = useState(2);
  const [endsOn, setEndsOn] = useState<string | null>(null);
  /** Which parts the model filled in, so they read dashed until he touches them. */
  const [drafted, setDrafted] = useState<Set<string>>(new Set());

  const [said, setSaid] = useState('');
  const [composing, setComposing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function compose(): Promise<void> {
    const text = said.trim();
    if (!text) return;
    setComposing(true);
    setError(null);
    try {
      const draft = await api.composeHomework({ said: text });
      setTitle(draft.title);
      setFields(draft.fields);
      setEndsOn(draft.endsOn);
      setDrafted(new Set(['title', 'ends', ...draft.fields.map((f) => f.id)]));
      setSaid('');
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Couldn't turn that into a form.");
    } finally {
      setComposing(false);
    }
  }

  function untouch(key: string): void {
    setDrafted((d) => {
      if (!d.has(key)) return d;
      const next = new Set(d);
      next.delete(key);
      return next;
    });
  }

  async function start(): Promise<void> {
    const t = title.trim();
    if (!t) return;
    setBusy(true);
    setError(null);
    try {
      const hw = await api.startHomework({
        title: t,
        fields,
        endsOn: endsOn ?? inWeeks(weeks),
      });
      navigate(`/homework/${hw.id}`, { replace: true });
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Couldn't start that.");
      setBusy(false);
    }
  }

  const until = endsOn ?? inWeeks(weeks);

  return (
    <div className="screen stack">
      <Link className="linkish back-link" to="/">
        <ArrowLeftIcon /> Today
      </Link>

      <header className="stack-sm stack">
        <h1>Therapy homework</h1>
        <p className="muted">
          A question you want put in front of you for a while. It appears on Today until the day you
          set, and then it stops. {/* See HomeworkIndex for why this changed: the add-on was built after
              this sentence was written, and it went on saying the opposite. */}
          <strong>
            Only a link carrying the &ldquo;{ADDON_CONTENTS.homework.name}&rdquo; add-on can see
            it
          </strong>{' '}
          — the words and the days. Every other link cannot tell it exists.
        </p>
      </header>

      {error && <div className="banner banner-error">{error}</div>}

      {canSummarise && (
        <section className="card stack-sm stack">
          <label htmlFor="hw-said">Say what you were asked, and it fills this in</label>
          <textarea
            id="hw-said"
            className="input"
            rows={2}
            value={said}
            onChange={(e) => setSaid(e.target.value)}
          />
          <button
            className="btn btn-secondary btn-block"
            disabled={composing || said.trim().length === 0}
            onClick={() => void compose()}
          >
            {composing ? 'Filling it in…' : 'Fill it in for me'}
          </button>
          <p className="faint" style={{ margin: 0 }}>
            It only ever fills these boxes, and never writes what you should say about anyone.
            Nothing is saved until you tap Start it.
          </p>
        </section>
      )}

      <div className="field">
        <label htmlFor="hw-title">What do you want to be asked?</label>
        <textarea
          id="hw-title"
          className={drafted.has('title') ? 'input input-drafted' : 'input'}
          rows={2}
          value={title}
          onChange={(e) => {
            setTitle(e.target.value);
            untouch('title');
          }}
        />
      </div>

      <HomeworkFieldList
        fields={fields}
        drafted={drafted}
        onChange={(next) => {
          setFields(next);
          next.forEach((f) => untouch(f.id));
        }}
      />

      <div className="field">
        <label htmlFor="hw-weeks">For how long?</label>
        <div className="chips" id="hw-weeks">
          {[1, 2, 4].map((w) => (
            <button
              key={w}
              className="chip"
              aria-pressed={endsOn === null && weeks === w}
              onClick={() => {
                setWeeks(w);
                setEndsOn(null);
                untouch('ends');
              }}
            >
              {w === 1 ? '1 week' : `${w} weeks`}
            </button>
          ))}
          {endsOn && (
            <span className={drafted.has('ends') ? 'chip chip-app' : 'chip'} aria-pressed="true">
              Until {formatUntil(endsOn)}
            </span>
          )}
        </div>
        <p className="faint" style={{ margin: 0 }}>
          It stops on {formatUntil(until)}, by itself.
        </p>
      </div>

      <button
        className="btn btn-primary btn-block"
        disabled={busy || title.trim().length === 0}
        onClick={() => void start()}
      >
        {busy ? 'Starting…' : 'Start it'}
      </button>
    </div>
  );
}

function inWeeks(weeks: number): string {
  const d = new Date();
  d.setDate(d.getDate() + weeks * 7);
  return d.toISOString().slice(0, 10);
}
